import React, { Dispatch, Reducer } from "react";
import { LeftPanel, RightPanel, zoomLevelDefault } from "./constants";
import {
  LocationData,
  MapData,
  LegendData,
  MapLayer,
  Children,
  AnalysisItem,
  LegendItem,
  AnalysisData,
  CrsLayer,
  ClimateItems,
  AnalyseModalData,
  EventsFilterForm,
  EventData,
  EventLegendData,
  SnackBarParams,
  Shape,
  AnalysisStore,
  DrawShapeData,
  DrawShapeSource
} from "./models";
import { getLanguage } from "./translations";
import {
  createDefaultColumnList,
  createDefaultFilterList,
  createDefaultShapList
} from "helpers/helpers";
import dayjs from "dayjs";

export interface DrawShapeLegend {
  shapeId: string;
  lineIndex: number;
  lineColor: string;
  lineName: string;
  description?: string;
}

const DEFAULT_DRAW_SHAPE_COLOR = "rgb(15, 77, 188)";

const getShapeStrokeColor = (shape: any, index: number): string => {
  const defaultColor = DEFAULT_DRAW_SHAPE_COLOR;
  const amapRef = shape?.aMapRef;

  if (!amapRef) {
    return defaultColor;
  }

  const optionsColor = amapRef.getOptions?.()?.strokeColor;
  if (typeof optionsColor === "string" && optionsColor.trim()) {
    return optionsColor;
  }

  const getterColor = amapRef.get?.("strokeColor");
  if (typeof getterColor === "string" && getterColor.trim()) {
    return getterColor;
  }

  return defaultColor;
};

const buildDrawShapeLegends = (
  shapes: DrawShapeData[],
  previousLegends: DrawShapeLegend[] = []
): DrawShapeLegend[] => {
  const previousLegendMap = new Map(
    previousLegends.map((legend) => [legend.shapeId, legend])
  );

  return shapes.map((shape, index) => {
    const previousLegend = previousLegendMap.get(shape.id);
    const lineName =
      shape.name?.trim() || previousLegend?.lineName || `Line ${index + 1}`;

    return {
      shapeId: shape.id,
      lineIndex: index + 1,
      lineColor: getShapeStrokeColor(shape, index),
      lineName,
      description: previousLegend?.description
    };
  });
};

const isSameShapeRecord = (left: any, right: any) => {
  if (!left || !right) {
    return false;
  }

  return (
    left.id === right.id ||
    left.id === right.shapeId ||
    left.shapeId === right.id ||
    (left.shapeId && left.shapeId === right.shapeId)
  );
};

const syncCurrentShapeFromShapes = (
  currentShape: any,
  shapes: DrawShapeData[]
) => {
  if (!currentShape) {
    return currentShape;
  }

  return shapes.find((shape) => isSameShapeRecord(currentShape, shape)) || currentShape;
};

const isImportedShapeSource = (source?: DrawShapeSource) =>
  source === DrawShapeSource.SHAPE_LINE ||
  source === DrawShapeSource.SHAPE_POLYGON;

const isLineShapeSource = (source?: DrawShapeSource) =>
  source === DrawShapeSource.LINE || source === DrawShapeSource.SHAPE_LINE;

const isPolygonShapeSource = (source?: DrawShapeSource) =>
  source === DrawShapeSource.POLYGON || source === DrawShapeSource.SHAPE_POLYGON;

export interface IAppArguments {
  type: AppAction;
  payload?:
    | string
    | LeftPanel
    | RightPanel
    | MapData
    | boolean
    | number[]
    | number
    | Children
    | LocationData[]
    | LocationData
    | LegendData[]
    | MapLayer[]
    | CrsLayer[]
    | AnalysisItem[]
    | ClimateItems[]
    | LegendItem[]
    | AnalysisData
    | EventData
    | AnalyseModalData
    | EventsFilterForm
    | EventLegendData[]
    | SnackBarParams
    | AnalysisStore
    | any;
}

export enum AppAction {
  setMenu = "SET_MENU",
  setLanguage = "SET_LANGUAGE",
  setLeftPanel = "SET_LEFT_PANEL",
  setRightPanel = "SET_RIGHT_PANEL",
  setMapData = "SET_MAP_DATA",
  setMapCenter = "SET_MAP_CENTER",
  setDeleteLocationSetModal = "SET_DELETE_LOCATION_SET_MODAL",
  setDeleteLocationModal = "SET_DELETE_LOCATION_MODAL",
  setSearchMapCenter = "SET_SEARCH_MAP_CENTER",
  setMapZoom = "SET_MAP_ZOOM",
  setAddressEncoding = "SET_ADDRESS_ENCODING",
  setAddressEncodingLoading = "SET_ADDRESS_ENCODING_LOADING",
  setAddressEncodingLoadingText = "SET_ADDRESS_ENCODING_LOADING_TEXT",
  setAddressEncodingData = "SET_ADDRESS_ENCODING_DATA",
  setAddressEncodingCandidatesData = "SET_ADDRESS_ENCODING_CANDIDATES_DATA",
  setAddressEncodingNoMatchData = "SET_ADDRESS_ENCODING_NO_MATCH_DATA",
  setAddressEncodingMatchData = "SET_ADDRESS_ENCODING_MATCH_DATA",
  setAddressData = "SET_ADDRESS_DATA",
  setLayerFloods = "SET_LAYER_FLOODS",
  setLayerEarthquakes = "SET_LAYER_EARTHQUAKES",
  setAnalyseModal = "SET_ANALYSE_MODAL",
  setSingleAnalyseModal = "SET_SINGLE_ANALYSE_MODAL",
  setMultipleAnalyseModal = "SET_MULTIPLE_ANALYSE_MODAL",
  setAnalyseShapeModal = "SET_ANALYSE_SHAPE_MODAL",
  setImportModal = "SET_IMPORT_MODAL",
  setErrorModal = "SET_ERROR_MODAL",
  setErrorData = "SET_ERROR_DATA",
  setShowLocationAnalysisDetails = "SET_SHOW_LOCATION_ANALYSIS_DETAILS",
  setImportMenu = "SET_IMPORT_MENU",
  setCreateModal = "SET_CREATE_MODAL",
  setEditModal = "SET_EDIT_MODAL",
  setLayerInfoModal = "SET_LAYER_INFO_MODAL",
  setTipModal = "SET_TIP_MODAL",
  setClimateRiskModal = "SET_CLIMATE_RISK_MODAL",
  setSingleLocationAnalysed = "SET_SINGLE_LOCATION_ANALYSED",
  setLocationSets = "SET_MULTIPLE_LOCATIONS",
  setMultipleLocationsLoadings = "SET_MULTIPLE_LOCATIONS_LOADINGS",
  setMultipleLocationsSetLoadings = "SET_MULTIPLE_LOCATIONS_SET_LOADINGS",
  setMultipleLocationsEdit = "SET_MULTIPLE_LOCATIONS_EDIT",
  setMultipleLocationsAnalysed = "SET_MULTIPLE_LOCATIONS_ANALYSED",
  setCurrentMultipleLocationsAnalysed = "SET_CURRENT_MULTIPLE_LOCATIONS_ANALYSED",
  setCurrentMultipleLocations = "SET_CURRENT_MULTIPLE_LOCATIONS",
  setImportMultipleLocations = "SET_IMPORT_MULTIPLE_LOCATIONS",
  setCurrentMultipleLocationInfo = "SET_CURRENT_MULTIPLE_LOCATION_INFO",
  setCurrentHiddenLocationsModal = "SET_HIDDEN_LOCATIONS_MODAL",
  setEditMultipleLocation = "SET_EDIT_MULTIPLE_LOCATION",
  setLayerData = "SET_LAYER_DATA",
  setLayerList = "SET_LYAER_LIST",
  updateLayerList = "UPDATE_LAYER_LIST",
  setCrsLayerList = "SET_CRS_LYAER_LIST",
  setFutureHazardLayerList = "SET_FUTURE_HAZARD_LAYER_LIST",
  setCrsComponentLayerList = "SET_CRS_COMPONENT_LAYER_LIST",
  setLayerLegend = "SET_LYAER_LEGEND",
  setSearchLocationInfo = "SET_SEARCH_LOCATION_INFO",
  setAnalysisLegends = "SET_ANALYSIS_LEGENDS",
  setAnalysisResults = "SET_ANALYSIS_RESULTS",
  setAnalysisData = "SET_ANALYSIS_DATA",
  addLayerLegend = "ADD_LAYER_LEGEND",
  deleteLayerLegend = "DELETE_LAYER_LEGEND",
  updateLayerLegend = "UPDATE_LAYER_LEGEND",
  setMapClickEvent = "SET_MAP_CLICK_Event",
  setBatchId = "SET_BATCH_ID",
  setFutureHazardBatchId = "SET_FUTURE_HAZARD_BATCH_ID",
  setCrsComponentBatchId = "SET_CRS_COMPONENT_BATCH_ID",
  setOption = "SET_OPTION",
  setLocationsSize = "SET_LOCATIONS_SIZE",
  setLocationSource = "SET_LOCATION_SOURCE",
  setJobId = "SET_JOB_ID",
  setHazardLegends = "SET_HAZARD_LEGENDS",
  setHazardMultipleAnalysisStore = "SET_HAZARD_MULTIPLE_ANALYSIS_STORE",
  setHazardMultipleAnalysisResult = "SET_HAZARD_MULTIPLE_ANALYSIS_RESULT",
  setClimateMultipleAnalysisResult = "SET_CLIMATE_MULTIPLE_ANALYSIS_RESULT",
  setEventAnalysisResult = "SET_EVENT_ANALYSIS_RESULT",
  setEventAnalysisStore = "SET_EVENT_ANALYSIS_STORE",
  setEventAnalysisLegend = "SET_EVENT_ANALYSIS_LEGEND",
  setHazardMultipleAnalysedLegends = "SET_HAZARD_MULTIPLE_ANALYSED_LEGENDS",
  setClimateMultipleAnalysedLegends = "SET_CLIMATE_MULTIPLE_ANALYSED_LEGENDS",
  setEventAnalysisStatus = "SET_EVENT_ANALYSIS_STATUS",
  setHazardMultipleAnalysisStatus = "SET_HAZARD_MULTIPLE_ANALYSIS_STATUS",
  setClimateMultipleAnalysisStatus = "SET_CLIMATE_MULTIPLE_ANALYSIS_STATUS",
  addSnackBarParams = "ADD_SNACKBAR_PARAMS",
  popSnackBarParams = "POP_SNACKBAR_PARAMS",
  setArrowLeft = "SET_ARROW_LEFT",
  setArrowRight = "SET_ARROW_RIGHT",
  setUserRoles = "SET_USER_ROLES",
  setUserInfo = "SET_USER_INFO",
  setSingleFathomPopup = "SET_SINGLE_FATHOM_POPUP",
  setMultipleFathomPopup = "SET_MULTIPLE_FATHOM_POPUP",
  setSelectLayer = "SET_SELECT_LAYER",
  setSingleAnalysisSelectedLayers = "SET_SINGLE_ANALYSIS_SELECTED_LAYERS",
  setAskAgain = "SET_ASK_AGAIN",
  setShapeFathomPopup = "SET_SHAPE_FATHOM_POPUP",
  setLineSelectLayer = "SET_LINE_SELECT_LAYER",
  setPolygonSelectLayer = "SET_POLYGON_SELECT_LAYER",
  setShapeAskAgain = "SET_SHAPE_ASK_AGAIN",
  setFathomPermissionDialog = "SET_FATHOM_PERMISSION_DIALOG",
  setSspData = "SET_SSP_DATA",
  setClimateRiskCategorySspData = "SET_CLIMATE_RISK_CATEGORY_SSP_DATA",
  setSelectedEvent = "SET_SELECTED_EVENT",
  setEventsList = "SET_EVENTS_LIST",
  setCurrentEventsLocationInfo = "SET_CURRENT_EVENTS_LOCATION_INFO",
  setEventsListStatus = "SET_EVENTS_LIST_Status",
  setShowTcTrack = "SET_SHOW_TC_TRACK",
  setFathomIconDialog = "SET_FATHOM_ICON_DIALOG",
  setEventsFilterForm = "SET_EVENTS_FILTER_FORM",
  setEventsLegend = "SET_EVENTS_LEGEND",
  addEventsLegend = "ADD_EVENTS_LEGEND",
  deleteEventsLegend = "DELETE_EVENTS_LEGEND",
  updateEventsLegend = "UPDATE_EVENTS_LEGEND",
  setSelectedEventsList = "SET_SELECTED_EVENTS_LIST",
  setTryAgainType = " SET_TRY_AGAIN_TYPE",
  setScaleType = "SET_SCALE_TYPE",
  setLocationColumnList = "SET_LOCATION_COLUMN_LIST",
  setLocationsFilterInfo = "SET_LOCATIONS_FILTER_INFO",
  setMultipleLocationsSearch = "SET_MULTIPLELOCATIONSSERCH",
  setUserNotifications = "SET_USER_NOTIFICATIONS",
  setDrawPolylines = "SET_DRAW_POLYLINES",
  setShapeSelectIndex = "SET_POLYLINE_SELECT_INDEX",
  setShapes = "SET_POLYLINES",
  selectShape = "SELECT_POLYLINE",
  deselectShape = "DESELECT_POLYLINE",
  enterShapeEditMode = "ENTER_POLYLINE_EDIT_MODE",
  exitShapeEditMode = "EXIT_POLYLINE_EDIT_MODE",
  updateShapeCoordinates = "UPDATE_POLYLINE_COORDINATES",
  deleteShape = "DELETE_POLYLINE",
  updateMyShape = "UPDATE_MY_SHAPE",
  setMyShapes = "SET_MY_SHAPES",
  setMyShapesListStatus = "SET_MY_SHAPES_LIST_STATUS",
  addMyShapes = "ADD_MY_SHAPES",
  deleteMyShapes = "DEL_MY_SHAPES",
  setCurrentShape = "SET_CURRENT_SHAPE",
  setLineAnalysisResult = "SET_LINE_ANALYSIS_RESULT",
  setLineAnalysisStatus = "SET_LINE_ANALYSIS_STATUS",
  setLineAnalysisLegends = "SET_LINE_ANALYSIS_LEGENDS",
  setLineAnalysisShapeId = "SET_LINE_ANALYSIS_SHAPE_ID",
  setLineJobId = "SET_LINE_JOBID",
  setLineAnalysisPanelVisible = "SET_LINE_ANALYSIS_PANEL_VISIBLE",
  setDrawLineLegendsLegends = "SET_DRAW_LINE_LEGENDS",
  addDrawLineLegend = "ADD_DRAW_LINE_LEGEND",
  deleteDrawLineLegend = "DELETE_DRAW_LINE_LEGEND",
  setPolygonAnalysisResult = "SET_POLYGON_ANALYSIS_RESULT",
  setPolygonLayerStatistics = "SET_POLYGON_LAYER_STATISTICS",
  setPolygonAnalysisStatus = "SET_POLYGON_ANALYSIS_STATUS",
  setPolygonAnalysisLegends = "SET_POLYGON_ANALYSIS_LEGENDS",
  setPolygonAnalysisShapeId = "SET_POLYGON_ANALYSIS_SHAPE_ID",
  setPolygonH3Resolution = "SET_POLYGON_H3_RESOLUTION",
  setPolygonExpandedLayerId = "SET_POLYGON_EXPANDED_LAYER_ID",
  setPolygonJobId = "SET_POLYGON_JOBID"
}

export interface AppState {
  menu: any[];
  language: string;
  leftPanel: LeftPanel;
  rightPanel: RightPanel;
  mapData: MapData;
  mapCenter?: number[];
  searchMapCenter?: number[];
  mapZoom: number;
  layerFloods: boolean;
  layerEarthquakes: boolean;
  analyseModal?: AnalyseModalData;
  singleAnalyseModal?: AnalyseModalData;
  multipleAnalyseModal?: AnalyseModalData;
  analyseShapeModal?: boolean;
  importModal?: boolean;
  errorModal?: boolean;
  errorData?: { row: number; message: string }[];
  showLocationAnalysisDetails?: boolean;
  importMenu: boolean;
  createModal?: boolean;
  editModal?: boolean;
  layerInfoModal?: Children;
  tipModal?: any;
  climateRiskModal?: boolean;
  singleLocationAnalysed?: boolean;
  locationSets?: LocationData[];
  multipleLocationsLoadings?: boolean;
  multipleLocationsSetLoadings?: boolean;
  multipleLocationsEdit?: boolean;
  multipleLocationsAnalysed?: boolean;
  importMultipleLocations?: LocationData;
  currentMultipleLocations?: LocationData;
  currentMultipleLocationsAnalysed?: any;
  currentMultipleLocationInfo?: any;
  currentHiddenLocationsModal?: any;
  editMultipleLocation?: any;
  vectorMapForeign?: string;
  layerData?: any;
  layerList?: MapLayer[];
  layerLegend?: LegendData[];
  crsLayerList?: CrsLayer[];
  futureHazardLayerList?: CrsLayer[];
  crsComponentLayerList?: CrsLayer[];
  searchLocationInfo?: any;
  analysisResults?: AnalysisItem[];
  analysisLegends?: LegendItem[];
  analysisData?: AnalysisData;
  mapClickEvent?: any;
  batchId?: string;
  futureHazardBatchId?: string;
  crsComponentBatchId?: string;
  option?: string;
  addressEncoding?: boolean;
  addressEncodingLoading?: boolean;
  addressEncodingLoadingText?: string;
  addressEncodingData?: any[];
  addressEncodingCandidatesData?: any[];
  addressEncodingNoMatchData?: any[];
  addressEncodingMatchData?: any[];
  addressData?: any[];
  locationsSize?: boolean;
  locationSource?: string;
  jobId?: string;
  hazardLegends?: LegendItem[];
  eventAnalysisResult?: AnalysisItem[];
  eventAnalysisStore?: AnalysisStore;
  eventAnalysisLegend?: LegendItem[];
  hazardMultipleAnalysisResult?: AnalysisItem[];
  hazardMultipleAnalysisStore?: AnalysisStore;
  climateMultipleAnalysisResult?: ClimateItems[];
  hazardMultipleAnalysedLegends?: LegendItem[];
  climateMultipleAnalysedLegends?: CrsLayer[];
  eventAnalysisStatus?: string;
  hazardMultipleAnalysisStatus?: string;
  climateMultipleAnalysisStatus?: string;
  deleteLocationSetModal?: any;
  deleteLocationModal?: any;
  snackBarParams?: SnackBarParams[];
  arrowLeft?: boolean;
  arrowRight?: boolean;
  userRoles?: string[];
  userInfo?: any;
  sspData?: any;
  climateRiskCategorySspData?: any;
  singleFathomPopup?: string;
  multipleFathomPopup?: string;
  selectLayer?: any[];
  singleAnalysisSelectedLayers?: string[][];
  askAgain?: boolean;
  shapeFathomPopup?: string;
  lineSelectLayer?: any[];
  polygonSelectLayer?: any[];
  shapeAskAgain?: boolean;
  fathomPermissionDialog?: any;
  selectedEvent?: EventData;
  showTcTrack: boolean;
  eventsList?: EventData[];
  currentEventsLocationInfo?: any;
  eventsListStatus: string;
  fathomIconDialog?: boolean;
  eventsFilterForm: EventsFilterForm;
  eventsLegend?: EventLegendData[];
  selectedEventsList: EventData[];
  tryAgainType?: string;
  scaleType?: string;
  locationsColumnList: any[];
  locationsFilterInfo: any[];
  multipleLocationsSearch: string;
  userNotifications?: any[];
  drawShapes: any[];
  shapeSelectIndex?: number;
  shapes: DrawShapeData[];
  selectedShapeId?: string;
  editingShapeId?: string;
  myShapes: Shape[];
  myShapesListStatus: string;
  currentShape?: any;
  lineAnalysisResult?: any;
  lineAnalysisLegends?: any;
  lineAnalysisStatus?: string;
  lineAnalysisShapeId?: string;
  lineJobId?: string;
  lineAnalysisPanelVisible?: boolean;
  drawLineLegendsLegends?: DrawShapeLegend[];
  polygonAnalysisResult?: any;
  polygonLayerStatistics?: any;
  polygonAnalysisLegends?: any;
  polygonAnalysisShapeId?: string;
  polygonH3Resolution?: number;
  polygonExpandedLayerId?: string;
  polygonAnalysisStatus?: string;
  polygonJobId?: string;
}

export const appInitialState: AppState = {
  menu: [],
  language: getLanguage() || "",
  leftPanel: LeftPanel.Empty,
  rightPanel: RightPanel.Empty,
  mapData: { zoom: 0, centerLat: 0, centerLon: 0 },
  mapZoom: zoomLevelDefault,
  layerFloods: false,
  layerEarthquakes: false,
  importMenu: false,
  multipleLocationsLoadings: true,
  showTcTrack: true,
  vectorMapForeign: "2D",
  option: "hazard",
  addressEncodingCandidatesData: [],
  addressEncodingNoMatchData: [],
  addressEncodingMatchData: [],
  eventsFilterForm: {
    fromDateTime: dayjs(new Date().setMonth(new Date().getMonth() - 3)).format(
      "YYYY-MM-DD"
    ),
    toDateTime: dayjs(new Date()).format("YYYY-MM-DD"),
    isShowAll: false,
    type: ["Cyclone", "Winterstorm", "Earthquake", "Flood", "Rainfall"],
    isNotRequest: false //是否不请求数据
  },
  selectedEventsList: [],
  eventsListStatus: "1", //1 loading 2 正常  3报错
  locationsColumnList: createDefaultColumnList(),
  locationsFilterInfo: createDefaultFilterList(),
  multipleLocationsSearch: "",
  drawShapes: [],
  shapeSelectIndex: undefined,
  shapes: [],
  selectedShapeId: undefined,
  editingShapeId: undefined,
  myShapes: createDefaultShapList(),
  myShapesListStatus: "1", //1 loading 2 正常  3报错
  drawLineLegendsLegends: []
};

export const appContextReducer: Reducer<AppState, IAppArguments> = (
  state: AppState = appInitialState,
  action: IAppArguments
): AppState => {
  switch (action.type) {
    case AppAction.setMenu:
      return {
        ...state,
        menu: action.payload as any[]
      };
    case AppAction.setAddressEncodingData:
      return {
        ...state,
        addressEncodingData: action.payload as any[]
      };
    case AppAction.setAddressEncodingLoadingText:
      return {
        ...state,
        addressEncodingLoadingText: action.payload as string
      };
    case AppAction.setAddressEncodingCandidatesData:
      return {
        ...state,
        addressEncodingCandidatesData: action.payload as any[]
      };
    case AppAction.setAddressEncodingNoMatchData:
      return {
        ...state,
        addressEncodingNoMatchData: action.payload as any[]
      };
    case AppAction.setAddressEncodingMatchData:
      return {
        ...state,
        addressEncodingMatchData: action.payload as any[]
      };
    case AppAction.setAddressEncoding:
      return {
        ...state,
        addressEncoding: action.payload as boolean
      };
    case AppAction.setAddressEncodingLoading:
      return {
        ...state,
        addressEncodingLoading: action.payload as boolean
      };
    case AppAction.setLanguage:
      return {
        ...state,
        language: action.payload as string
      };
    case AppAction.setLeftPanel:
      return {
        ...state,
        leftPanel: action.payload as LeftPanel
      };
    case AppAction.setRightPanel:
      return {
        ...state,
        rightPanel: action.payload as RightPanel,
        // 切到线分析面板时自动标记为可见
        lineAnalysisPanelVisible:
          action.payload === RightPanel.Line || action.payload === RightPanel.Polygon
            ? true
            : state.currentShape
              ? state.lineAnalysisPanelVisible
              : false
      };
    case AppAction.setMapData:
      return {
        ...state,
        mapData: action.payload as MapData
      };
    case AppAction.setMapCenter:
      return {
        ...state,
        mapCenter: action.payload as number[]
      };
    case AppAction.setSearchMapCenter:
      return {
        ...state,
        searchMapCenter: action.payload as number[]
      };
    case AppAction.setMapZoom:
      return {
        ...state,
        mapZoom: action.payload as number
      };
    case AppAction.setLayerFloods:
      return {
        ...state,
        layerFloods: action.payload as boolean
      };
    case AppAction.setLayerEarthquakes:
      return {
        ...state,
        layerEarthquakes: action.payload as boolean
      };
    case AppAction.setAnalyseModal:
      const analyseModalPayload = action.payload as AnalyseModalData | undefined;
      return {
        ...state,
        analyseModal: analyseModalPayload,
        singleAnalyseModal:
          analyseModalPayload?.type === "1"
            ? analyseModalPayload
            : analyseModalPayload === undefined
              ? undefined
              : state.singleAnalyseModal,
        multipleAnalyseModal:
          analyseModalPayload?.type === "2"
            ? analyseModalPayload
            : analyseModalPayload === undefined
              ? undefined
              : state.multipleAnalyseModal
      };
    case AppAction.setSingleAnalyseModal:
      return {
        ...state,
        singleAnalyseModal: action.payload as AnalyseModalData,
        analyseModal: action.payload as AnalyseModalData
      };
    case AppAction.setMultipleAnalyseModal:
      return {
        ...state,
        multipleAnalyseModal: action.payload as AnalyseModalData,
        analyseModal: action.payload as AnalyseModalData
      };
    case AppAction.setAnalyseShapeModal:
      return {
        ...state,
        analyseShapeModal: action.payload as boolean
      };
    case AppAction.setImportModal:
      return {
        ...state,
        importModal: action.payload as boolean
      };
    case AppAction.setErrorModal:
      return {
        ...state,
        errorModal: action.payload as boolean
      };
    case AppAction.setErrorData:
      return {
        ...state,
        errorData: action.payload as { row: number; message: string }[]
      };
    case AppAction.setShowLocationAnalysisDetails:
      return {
        ...state,
        showLocationAnalysisDetails: action.payload as boolean
      };
    case AppAction.setImportMenu:
      return {
        ...state,
        importMenu: action.payload as boolean
      };
    case AppAction.setCreateModal:
      return {
        ...state,
        createModal: action.payload as boolean
      };
    case AppAction.setEditModal:
      return {
        ...state,
        editModal: action.payload as boolean
      };
    case AppAction.setLayerInfoModal:
      return {
        ...state,
        layerInfoModal: action.payload as Children
      };
    case AppAction.setTipModal:
      return {
        ...state,
        tipModal: action.payload as any
      };
    case AppAction.setClimateRiskModal:
      return {
        ...state,
        climateRiskModal: action.payload as boolean
      };
    case AppAction.setSingleLocationAnalysed:
      return {
        ...state,
        singleLocationAnalysed: action.payload as boolean
      };
    case AppAction.setLocationSets:
      return {
        ...state,
        locationSets: action.payload as LocationData[]
      };
    case AppAction.setMultipleLocationsLoadings:
      return {
        ...state,
        multipleLocationsLoadings: action.payload as boolean
      };
    case AppAction.setMultipleLocationsSetLoadings:
      return {
        ...state,
        multipleLocationsSetLoadings: action.payload as boolean
      };
    case AppAction.setAddressData:
      return {
        ...state,
        addressData: action.payload as any[]
      };
    case AppAction.setMultipleLocationsEdit:
      return {
        ...state,
        multipleLocationsEdit: action.payload as boolean
      };
    case AppAction.setMultipleLocationsAnalysed:
      return {
        ...state,
        multipleLocationsAnalysed: action.payload as boolean
      };
    case AppAction.setCurrentMultipleLocationsAnalysed:
      return {
        ...state,
        currentMultipleLocationsAnalysed: action.payload as any
      };
    case AppAction.setCurrentMultipleLocations:
      return {
        ...state,
        currentMultipleLocations: action.payload as LocationData
      };
    case AppAction.setImportMultipleLocations:
      return {
        ...state,
        importMultipleLocations: action.payload as LocationData
      };
    case AppAction.setCurrentMultipleLocationInfo:
      return {
        ...state,
        currentMultipleLocationInfo: action.payload as any
      };
    case AppAction.setCurrentHiddenLocationsModal:
      return {
        ...state,
        currentHiddenLocationsModal: action.payload
      };
    case AppAction.setEditMultipleLocation:
      return {
        ...state,
        editMultipleLocation: action.payload as any
      };
    case AppAction.setLayerData:
      return {
        ...state,
        layerData: action.payload as any
      };
    case AppAction.setLayerList:
      return {
        ...state,
        layerList: action.payload as MapLayer[]
      };
    case AppAction.updateLayerList:
      if (state.layerList) {
        state.layerList = state.layerList.map((layer) => {
          if (layer.name === action.payload.group) {
            layer.children.forEach((layerItem) => {
              if (layerItem.id === action.payload.id) {
                layerItem = action.payload;
              }

              return layerItem;
            });
          }
          return layer;
        });
      }
      return {
        ...state
      };
    case AppAction.setLayerLegend:
      return {
        ...state,
        layerLegend: action.payload as LegendData[]
      };
    case AppAction.setCrsLayerList:
      return {
        ...state,
        crsLayerList: action.payload as CrsLayer[]
      };
    case AppAction.setFutureHazardLayerList:
      return {
        ...state,
        futureHazardLayerList: action.payload as CrsLayer[]
      };
    case AppAction.setCrsComponentLayerList:
      return {
        ...state,
        crsComponentLayerList: action.payload as CrsLayer[]
      };
    case AppAction.addLayerLegend:
      if (state.layerLegend === undefined) {
        state.layerLegend = [action.payload as LegendData];
      } else {
        state.layerLegend = [
          action.payload as LegendData,
          ...state.layerLegend
        ];
      }
      return {
        ...state
      };
    case AppAction.deleteLayerLegend:
      if (state.layerLegend === undefined) {
        state.layerLegend = [];
      } else {
        state.layerLegend = state.layerLegend.filter(
          (legend) => legend.layerId !== action.payload.id
        );
      }
      return {
        ...state
      };
    case AppAction.updateLayerLegend:
      if (state.layerLegend) {
        state.layerLegend = state.layerLegend.map((legend) => {
          if (legend.layerId === action.payload.id) {
            legend.layerInfo = action.payload;
          }
          return legend;
        });
      }
      return {
        ...state
      };
    case AppAction.setSearchLocationInfo:
      return {
        ...state,
        searchLocationInfo: action.payload
      };
    case AppAction.setAnalysisResults:
      return {
        ...state,
        analysisResults: action.payload as AnalysisItem[]
      };
    case AppAction.setAnalysisLegends:
      return {
        ...state,
        analysisLegends: action.payload as LegendItem[]
      };
    case AppAction.setAnalysisData:
      return {
        ...state,
        analysisData: action.payload as AnalysisData
      };
    case AppAction.setMapClickEvent:
      return {
        ...state,
        mapClickEvent: action.payload as any
      };
    case AppAction.setBatchId:
      return {
        ...state,
        batchId: action.payload as string
      };
    case AppAction.setFutureHazardBatchId:
      return {
        ...state,
        futureHazardBatchId: action.payload as string
      };
    case AppAction.setCrsComponentBatchId:
      return {
        ...state,
        crsComponentBatchId: action.payload as string
      };
    case AppAction.setOption:
      return {
        ...state,
        option: action.payload as string
      };
    case AppAction.setLocationsSize:
      return {
        ...state,
        locationsSize: action.payload as boolean
      };
    case AppAction.setLocationSource:
      return {
        ...state,
        locationSource: action.payload as string
      };
    case AppAction.setJobId:
      return {
        ...state,
        jobId: action.payload as string
      };
    case AppAction.setHazardLegends:
      return {
        ...state,
        hazardLegends: action.payload as LegendItem[]
      };
    case AppAction.setHazardMultipleAnalysisResult:
      return {
        ...state,
        hazardMultipleAnalysisResult: action.payload as AnalysisItem[]
      };
    case AppAction.setHazardMultipleAnalysisStore:
      return {
        ...state,
        hazardMultipleAnalysisStore: action.payload as AnalysisStore
      };
    case AppAction.setClimateMultipleAnalysisResult:
      return {
        ...state,
        climateMultipleAnalysisResult: action.payload as ClimateItems[]
      };
    case AppAction.setShowTcTrack:
      return {
        ...state,
        showTcTrack: action.payload as boolean
      };
    case AppAction.setEventAnalysisResult:
      return {
        ...state,
        eventAnalysisResult: action.payload as AnalysisItem[]
      };
    case AppAction.setEventAnalysisStore:
      return {
        ...state,
        eventAnalysisStore: action.payload as AnalysisStore
      };
    case AppAction.setEventAnalysisLegend:
      return {
        ...state,
        eventAnalysisLegend: action.payload as LegendItem[]
      };
    case AppAction.setHazardMultipleAnalysedLegends:
      return {
        ...state,
        hazardMultipleAnalysedLegends: action.payload as LegendItem[]
      };
    case AppAction.setClimateMultipleAnalysedLegends:
      return {
        ...state,
        climateMultipleAnalysedLegends: action.payload as CrsLayer[]
      };
    case AppAction.setEventAnalysisStatus:
      return {
        ...state,
        eventAnalysisStatus: action.payload as string
      };
    case AppAction.setHazardMultipleAnalysisStatus:
      return {
        ...state,
        hazardMultipleAnalysisStatus: action.payload as string
      };
    case AppAction.setClimateMultipleAnalysisStatus:
      return {
        ...state,
        climateMultipleAnalysisStatus: action.payload as string
      };
    case AppAction.setDeleteLocationSetModal:
      return {
        ...state,
        deleteLocationSetModal: action.payload as any
      };
    case AppAction.setDeleteLocationModal:
      return {
        ...state,
        deleteLocationModal: action.payload as any
      };
    case AppAction.addSnackBarParams:
      return {
        ...state,
        snackBarParams: [
          ...(state.snackBarParams || []),
          action.payload as SnackBarParams
        ]
      };
    case AppAction.popSnackBarParams:
      return {
        ...state,
        snackBarParams: (state.snackBarParams ?? []).slice(1)
      };
    case AppAction.setArrowLeft:
      return {
        ...state,
        arrowLeft: action.payload as boolean
      };
    case AppAction.setArrowRight:
      return {
        ...state,
        arrowRight: action.payload as boolean
      };
    case AppAction.setUserRoles:
      return {
        ...state,
        userRoles: action.payload as string[]
      };
    case AppAction.setUserInfo:
      return {
        ...state,
        userInfo: action.payload as any
      };
    case AppAction.setSingleFathomPopup:
      return {
        ...state,
        singleFathomPopup: action.payload as string
      };
    case AppAction.setMultipleFathomPopup:
      return {
        ...state,
        multipleFathomPopup: action.payload as string
      };
    case AppAction.setSelectLayer:
      return {
        ...state,
        selectLayer: action.payload as any[]
      };
    case AppAction.setSingleAnalysisSelectedLayers:
      return {
        ...state,
        singleAnalysisSelectedLayers: action.payload as string[][]
      };
    case AppAction.setAskAgain:
      return {
        ...state,
        askAgain: action.payload as boolean
      };
    case AppAction.setShapeFathomPopup:
      return {
        ...state,
        shapeFathomPopup: action.payload as string
      };
    case AppAction.setLineSelectLayer:
      return {
        ...state,
        lineSelectLayer: action.payload as any[]
      };
    case AppAction.setPolygonSelectLayer:
      return {
        ...state,
        polygonSelectLayer: action.payload as any[]
      };
    case AppAction.setShapeAskAgain:
      return {
        ...state,
        shapeAskAgain: action.payload as boolean
      };
    case AppAction.setSelectedEvent:
      return {
        ...state,
        selectedEvent: action.payload as EventData
      };
    case AppAction.setSspData:
      return {
        ...state,
        sspData: action.payload
      };
    case AppAction.setClimateRiskCategorySspData:
      return {
        ...state,
        climateRiskCategorySspData: action.payload
      };
    case AppAction.setFathomPermissionDialog:
      return {
        ...state,
        fathomPermissionDialog: action.payload as any
      };
    case AppAction.setEventsList:
      return {
        ...state,
        eventsList: action.payload as EventData[]
      };
    case AppAction.setCurrentEventsLocationInfo:
      return {
        ...state,
        currentEventsLocationInfo: action.payload as any
      };
    case AppAction.setEventsListStatus:
      return {
        ...state,
        eventsListStatus: action.payload as string
      };
    case AppAction.setFathomIconDialog:
      return {
        ...state,
        fathomIconDialog: action.payload as boolean
      };
    case AppAction.setEventsFilterForm:
      return {
        ...state,
        eventsFilterForm: action.payload as EventsFilterForm
      };
    case AppAction.setEventsLegend:
      return {
        ...state,
        eventsLegend: action.payload as EventLegendData[]
      };
    case AppAction.addEventsLegend:
      if (state.eventsLegend === undefined) {
        state.eventsLegend = [action.payload as EventLegendData];
      } else {
        state.eventsLegend = [
          action.payload as EventLegendData,
          ...state.eventsLegend
        ];
      }
      return {
        ...state
      };
    case AppAction.deleteEventsLegend:
      if (state.eventsLegend === undefined) {
        state.eventsLegend = [];
      } else {
        state.eventsLegend = state.eventsLegend.filter(
          (legend) => legend.layerId !== action.payload.layerId
        );
      }
      return {
        ...state
      };
    case AppAction.updateEventsLegend:
      if (state.eventsLegend) {
        state.eventsLegend = state.eventsLegend.map((legend) => {
          if (legend.layerId === action.payload.layerId) {
            legend.layerInfo = action.payload;
          }
          return legend;
        });
      }
      return {
        ...state
      };
    case AppAction.setSelectedEventsList:
      return {
        ...state,
        selectedEventsList: action.payload as EventData[]
      };
    case AppAction.setTryAgainType:
      return {
        ...state,
        tryAgainType: action.payload as string
      };
    case AppAction.setScaleType:
      return {
        ...state,
        scaleType: action.payload as string
      };
    case AppAction.setLocationColumnList:
      return {
        ...state,
        locationsColumnList: action.payload as any[]
      };
    case AppAction.setLocationsFilterInfo:
      return {
        ...state,
        locationsFilterInfo: action.payload as any[]
      };
    case AppAction.setMultipleLocationsSearch:
      return {
        ...state,
        multipleLocationsSearch: action.payload as string
      };
    case AppAction.setUserNotifications:
      return {
        ...state,
        userNotifications: action.payload as any[]
      };
    case AppAction.setDrawPolylines:
      return {
        ...state,
        drawShapes: action.payload as any[]
      };
    case AppAction.setShapeSelectIndex:
      return {
        ...state,
        shapeSelectIndex: action.payload as number
      };
    case AppAction.updateMyShape:
      return {
        ...state,
        myShapes: state.myShapes.map((shape) =>
          shape.id === action.payload.id
            ? {
                ...shape,
                name: action.payload.name,
                type: action.payload.type,
                hasShapeAnalysed:
                  action.payload.hasShapeAnalysed ?? shape.hasShapeAnalysed
              }
            : shape
        )
      };

    case AppAction.setMyShapes:
      return {
        ...state,
        myShapes: action.payload as Shape[]
      };

    case AppAction.setMyShapesListStatus:
      return {
        ...state,
        myShapesListStatus: action.payload as string
      };

    case AppAction.addMyShapes:
      return {
        ...state,
        myShapes: [...state.myShapes, action.payload]
      };

    case AppAction.deleteMyShapes:
      return {
        ...state,
        myShapes: state.myShapes.filter(
          (shape) => !action.payload.includes(shape.id)
        )
      };

    case AppAction.setShapes:
      const nextShapes = action.payload as DrawShapeData[];
      return {
        ...state,
        shapes: nextShapes,
        currentShape: syncCurrentShapeFromShapes(state.currentShape, nextShapes),
        drawLineLegendsLegends: buildDrawShapeLegends(
          nextShapes,
          state.drawLineLegendsLegends
        )
      };

    case AppAction.selectShape:
      const selectedShapes = state.shapes.map((pl) =>
        pl.id === action.payload
          ? { ...pl, state: "selected" as any }
          : { ...pl, state: "idle" as any }
      );
      const selectedShape = selectedShapes.find((pl) => pl.id === action.payload);
      return {
        ...state,
        selectedShapeId: action.payload as string,
        editingShapeId: undefined,
        shapes: selectedShapes,
        currentShape:
          selectedShape && isSameShapeRecord(state.currentShape, selectedShape)
            ? selectedShape
            : state.currentShape,
        drawLineLegendsLegends: buildDrawShapeLegends(
          selectedShapes,
          state.drawLineLegendsLegends
        )
      };

    case AppAction.deselectShape:
      const deselectedShapes = state.shapes.map((pl) =>
        pl.state === "editing" || pl.state === "selected"
          ? { ...pl, state: "idle" as any }
          : pl
      );
      return {
        ...state,
        selectedShapeId: undefined,
        editingShapeId: undefined,
        shapes: deselectedShapes,
        drawLineLegendsLegends: buildDrawShapeLegends(
          deselectedShapes,
          state.drawLineLegendsLegends
        )
      };

    case AppAction.enterShapeEditMode:
      const editingShapes = state.shapes.map((pl) =>
        pl.id === action.payload
          ? { ...pl, state: "editing" as any }
          : { ...pl, state: "idle" as any }
      );
      return {
        ...state,
        editingShapeId: action.payload as string,
        shapes: editingShapes,
        drawLineLegendsLegends: buildDrawShapeLegends(
          editingShapes,
          state.drawLineLegendsLegends
        )
      };

    case AppAction.exitShapeEditMode:
      const exitEditingShapes = state.shapes.map((pl) =>
        pl.id === state.editingShapeId
          ? { ...pl, state: "selected" as any }
          : pl
      );
      return {
        ...state,
        editingShapeId: undefined,
        shapes: exitEditingShapes,
        drawLineLegendsLegends: buildDrawShapeLegends(
          exitEditingShapes,
          state.drawLineLegendsLegends
        )
      };

    case AppAction.updateShapeCoordinates:
      const updatedCoordinateShapes = state.shapes.map((pl) =>
        pl.id === action.payload.id
          ? { ...pl, coordinates: action.payload.coordinates }
          : pl
      );
      return {
        ...state,
        shapes: updatedCoordinateShapes,
        currentShape: syncCurrentShapeFromShapes(
          state.currentShape,
          updatedCoordinateShapes
        ),
        drawLineLegendsLegends: buildDrawShapeLegends(
          updatedCoordinateShapes,
          state.drawLineLegendsLegends
        )
      };

    case AppAction.deleteShape:
      const deletedShapeId = action.payload as string;
      const deletedShape = state.shapes.find(
        (pl) => pl.id === deletedShapeId
      );
      const deletedMyShapeId = deletedShape?.shapeId;
      const currentShape = state.currentShape as any;
      const isDeletingCurrentShape = !!currentShape && (
        currentShape.id === deletedShapeId ||
        currentShape.shapeId === deletedShapeId ||
        (!!deletedMyShapeId && currentShape.id === deletedMyShapeId) ||
        (!!deletedMyShapeId && currentShape.shapeId === deletedMyShapeId)
      );
      const deletedShapeSource = deletedShape?.source;
      const shouldPreserveImportedShapeContext =
        isDeletingCurrentShape && isImportedShapeSource(deletedShapeSource);
      const shouldClearLineAnalysis =
        isDeletingCurrentShape &&
        isLineShapeSource(deletedShapeSource) &&
        deletedShapeSource !== DrawShapeSource.SHAPE_LINE;
      const shouldClearPolygonAnalysis =
        isDeletingCurrentShape &&
        isPolygonShapeSource(deletedShapeSource) &&
        deletedShapeSource !== DrawShapeSource.SHAPE_POLYGON;
      const remainingShapes = state.shapes.filter((pl) => pl.id !== deletedShapeId);

      return {
        ...state,
        shapes: remainingShapes,
        drawLineLegendsLegends: buildDrawShapeLegends(
          remainingShapes,
          state.drawLineLegendsLegends
        ),
        lineAnalysisPanelVisible:
          isDeletingCurrentShape && !shouldPreserveImportedShapeContext
            ? false
            : state.lineAnalysisPanelVisible,
        selectedShapeId:
          state.selectedShapeId === deletedShapeId
            ? undefined
            : state.selectedShapeId,
        editingShapeId:
          state.editingShapeId === deletedShapeId
            ? undefined
            : state.editingShapeId,
        currentShape:
          isDeletingCurrentShape && !shouldPreserveImportedShapeContext
            ? undefined
            : state.currentShape,
        lineAnalysisResult: shouldClearLineAnalysis
          ? undefined
          : state.lineAnalysisResult,
        lineAnalysisLegends: shouldClearLineAnalysis
          ? undefined
          : state.lineAnalysisLegends,
        lineAnalysisStatus: shouldClearLineAnalysis
          ? undefined
          : state.lineAnalysisStatus,
        lineAnalysisShapeId: shouldClearLineAnalysis
          ? undefined
          : state.lineAnalysisShapeId,
        lineJobId: shouldClearLineAnalysis ? undefined : state.lineJobId,
        polygonAnalysisResult: shouldClearPolygonAnalysis
          ? undefined
          : state.polygonAnalysisResult,
        polygonLayerStatistics: shouldClearPolygonAnalysis
          ? undefined
          : state.polygonLayerStatistics,
        polygonAnalysisLegends: shouldClearPolygonAnalysis
          ? undefined
          : state.polygonAnalysisLegends,
        polygonAnalysisShapeId: shouldClearPolygonAnalysis
          ? undefined
          : state.polygonAnalysisShapeId,
        polygonAnalysisStatus: shouldClearPolygonAnalysis
          ? undefined
          : state.polygonAnalysisStatus,
        polygonH3Resolution: shouldClearPolygonAnalysis
          ? undefined
          : state.polygonH3Resolution,
        polygonExpandedLayerId: shouldClearPolygonAnalysis
          ? undefined
          : state.polygonExpandedLayerId,
        polygonJobId: shouldClearPolygonAnalysis
          ? undefined
          : state.polygonJobId
      };

    case AppAction.setCurrentShape:
      const nextCurrentShape = action.payload as any;
      const shouldClearPolygonExpandedByShapeSwitch =
        !!nextCurrentShape &&
        !!state.polygonExpandedLayerId &&
        !!state.polygonAnalysisShapeId &&
        nextCurrentShape.id !== state.polygonAnalysisShapeId &&
        nextCurrentShape.shapeId !== state.polygonAnalysisShapeId;
      return {
        ...state,
        currentShape: nextCurrentShape,
        polygonExpandedLayerId: shouldClearPolygonExpandedByShapeSwitch
          ? undefined
          : state.polygonExpandedLayerId,
        // 清除 currentShape 时同步重置可见标记
        lineAnalysisPanelVisible: action.payload ? state.lineAnalysisPanelVisible : false
      };
    case AppAction.setLineAnalysisResult:
      return {
        ...state,
        lineAnalysisResult: action.payload as any
      };
    case AppAction.setLineAnalysisLegends:
      return {
        ...state,
        lineAnalysisLegends: action.payload as any
      };
    case AppAction.setLineAnalysisStatus:
      return {
        ...state,
        lineAnalysisStatus: action.payload as string
      };
    case AppAction.setLineAnalysisShapeId:
      return {
        ...state,
        lineAnalysisShapeId: action.payload as string | undefined
      };
    case AppAction.setLineJobId:
      return {
        ...state,
        lineJobId: action.payload as string
      };
    case AppAction.setLineAnalysisPanelVisible:
      return {
        ...state,
        lineAnalysisPanelVisible: action.payload as boolean
      };
    case AppAction.setPolygonAnalysisResult:
      return {
        ...state,
        polygonAnalysisResult: action.payload as any,
        polygonExpandedLayerId: undefined
      };
    case AppAction.setPolygonLayerStatistics:
      return {
        ...state,
        polygonLayerStatistics: action.payload as any
      };
    case AppAction.setPolygonAnalysisLegends:
      return {
        ...state,
        polygonAnalysisLegends: action.payload as any
      };
    case AppAction.setPolygonH3Resolution:
      return {
        ...state,
        polygonH3Resolution: action.payload as number | undefined
      };
    case AppAction.setPolygonExpandedLayerId:
      return {
        ...state,
        polygonExpandedLayerId: action.payload as string | undefined
      };
    case AppAction.setPolygonAnalysisStatus:
      return {
        ...state,
        polygonAnalysisStatus: action.payload as string
      };
    case AppAction.setPolygonAnalysisShapeId:
      return {
        ...state,
        polygonAnalysisShapeId: action.payload as string | undefined
      };
    case AppAction.setPolygonJobId:
      return {
        ...state,
        polygonJobId: action.payload as string
      };
    case AppAction.setDrawLineLegendsLegends:
      return {
        ...state,
        drawLineLegendsLegends: action.payload as DrawShapeLegend[]
      };
    case AppAction.addDrawLineLegend:
      return {
        ...state,
        drawLineLegendsLegends: [
          ...(state.drawLineLegendsLegends || []),
          action.payload as DrawShapeLegend
        ]
      };
    case AppAction.deleteDrawLineLegend:
      return {
        ...state,
        drawLineLegendsLegends: (state.drawLineLegendsLegends || []).filter(
          (l) => l.shapeId !== action.payload
        )
      };
    default:
      return state;
  }
};

export const AppContext = React.createContext<
  [AppState, Dispatch<IAppArguments>]
>([
  appInitialState,
  (action: IAppArguments) => appContextReducer(appInitialState, action)
]);

export const setLeftPanel = (
  dispatch: React.Dispatch<IAppArguments>,
  leftPanel: LeftPanel
): void => {
  dispatch({ type: AppAction.setLeftPanel, payload: leftPanel });
};
